/**
 * Dominoes orchestrator. Renders a 2-player game (you vs CPU). Mode is
 * supplied via the spec:
 *
 *   - 'block' — no boneyard. If you can't play, you pass. Two passes in a
 *     row → blocked → lower pip total wins.
 *   - 'draw' — face-down boneyard sits to the left of the table. If you
 *     can't play, you draw from the boneyard until you can (or it's empty).
 *
 * Other UI behavior is shared:
 *   - Player's hand (face-up) along the front edge.
 *   - CPU's hand (face-down) along the back edge.
 *   - Chain runs horizontally through the middle, snaking with L-bends.
 *   - Click a hand tile to play it. Single-end matches auto-snap; double-end
 *     matches lift the tile and present two glowing rings — pick a side.
 */

import {
  CanvasTexture,
  Mesh,
  MeshBasicMaterial,
  NoColorSpace,
  Object3D,
  PlaneGeometry,
  SRGBColorSpace,
  TextureLoader,
  type Material,
} from 'three';
import { createPointerPicker, findUserDataAncestor } from '../picker.js';
import {
  dealGame,
  legalPlaysFor,
  mustDraw,
  mustPass,
  tryDraw,
  tryPass,
  tryPlay,
  type DominoGameState,
  type Mode,
  type Player,
  type Side,
} from '../../../engine/dominoes/game.js';
import { pickCpuMove } from '../../../engine/dominoes/cpu.js';
import {
  canPlayLeft,
  canPlayRight,
  chainLeftEnd,
  chainRightEnd,
  playLeft as chainPlayLeft,
  playRight as chainPlayRight,
} from '../../../engine/dominoes/chain.js';
import { layoutChain2D, type ChainSlot } from './chain-layout.js';
import type { Domino } from '../../../engine/dominoes/domino.js';
import { handPipTotal } from '../../../engine/dominoes/domino.js';
import { createScene } from '../scene.js';
import { createTable } from '../table.js';
import { createShadowPass } from '../shadow-pass.js';
import {
  hasActiveTweens,
  tweenArc,
  tweenRotation,
  tweenScale,
  tweenTo,
  updateTweens,
} from '../anim.js';
import {
  DOM_D,
  DOM_GAP,
  DOM_LONG,
  DOM_PITCH_SHORT,
  DOM_SHORT,
  createDominoFaceMaterial,
  createDominoMesh,
  setFaceDown,
} from './domino-mesh.js';
import { createDominoHud } from './domino-hud.js';
import { playInvalid, playMatch, playSelect, playShuffle, unlockAudio } from '../../../audio/sounds.js';

// ── World layout constants ─────────────────────────────────────────────
const PLAYER_Z = 5.4;        // front-of-table hand row z
const CPU_Z = -5.4;          // back-of-table hand row z
const HAND_HOVER_LIFT = 0.05;
const SELECT_LIFT = 0.18;    // bigger lift for two-end-pick selection
const PLAY_DURATION_MS = 380;
const CPU_DELAY_MS = 750;
const PASS_DELAY_MS = 750;

// Chain layout — single-row width budget before turning a 90° corner.
// Picked so a typical opening run of ~6 non-double tiles fits before the
// chain turns; the snake then makes the chain readable for long games
// without the camera having to zoom way out.
const CHAIN_MAX_ROW = 12.5;
const CHAIN_ROW_GAP = 3.0;

// Boneyard (draw mode only) — face-down stack to the LEFT of the chain.
const BONEYARD_X = -(CHAIN_MAX_ROW / 2 + 1.6);
const BONEYARD_Z = 0;
const BONEYARD_STACK_OFFSET = DOM_D * 0.6;   // y-step between piled tiles

interface TileTarget {
  x: number;
  y: number;
  z: number;
  rotY: number;
  faceDown: boolean;
}

function sortHand(hand: readonly Domino[]): Domino[] {
  // Stable visual order: [low, high] ascending.
  return hand.slice().sort((a, b) => a.low - b.low || a.high - b.high);
}

const CHAIN_LAYOUT_OPTS = {
  longSide: DOM_LONG,
  shortSide: DOM_SHORT,
  gap: DOM_GAP,
  rowGap: CHAIN_ROW_GAP,
  maxRowWidth: CHAIN_MAX_ROW,
};

export interface DominoSpec {
  name: string;
  mode: Mode;
}

export async function mountDominoes(spec: DominoSpec): Promise<void> {
  const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('missing #stage canvas');

  const { scene, camera, renderer, loadHDRI, setBounds } = createScene(canvas);

  // Felt: keep the same green as the mahjong felt. CLAUDE.md mentions
  // walnut for dominoes future — leaving as a polish item.
  const loader = new TextureLoader();
  const feltNormal = loader.load('/textures/felt/normal.jpg', undefined, undefined, () => {});
  feltNormal.colorSpace = NoColorSpace;
  const feltRough = loader.load('/textures/felt/rough.jpg', undefined, undefined, () => {});
  feltRough.colorSpace = NoColorSpace;
  scene.add(createTable({
    feltColor: 0x07391f,
    feltSheenColor: 0x0e6638,
    feltNormal,
    feltRoughness: feltRough,
    textureRepeat: 8,
    normalScale: 0.9,
  }));

  loadHDRI('/hdri/studio_small_09_1k.hdr').catch((err) => {
    console.warn('HDRI load failed', err);
  });

  // ── State ────────────────────────────────────────────────────────────
  let state: DominoGameState = dealGame({ mode: spec.mode });
  let busy = false;

  // Stable display order per player — sort once per deal.
  let displayOrder: [Domino[], Domino[]] = [
    sortHand(state.hands[0]),
    sortHand(state.hands[1]),
  ];

  const meshById = new Map<string, Object3D>();
  const idByMesh = new Map<Object3D, string>();
  const dominoById = new Map<string, Domino>();
  for (const d of [...state.hands[0], ...state.hands[1], ...state.boneyard]) {
    dominoById.set(d.id, d);
  }

  // Cache the most recent layout so target lookups don't re-walk the chain.
  let chainSlots: ChainSlot[] = [];
  function recomputeChain() {
    chainSlots = layoutChain2D(state.chain.placements, CHAIN_LAYOUT_OPTS).slots;
  }
  recomputeChain();

  // ── Bounds (recomputed when chain grows) ─────────────────────────────
  function updateBounds() {
    const { bbox } = layoutChain2D(state.chain.placements, CHAIN_LAYOUT_OPTS);
    const handWidth = 7 * DOM_PITCH_SHORT;
    const handDepth = (PLAYER_Z + DOM_LONG / 2) * 2;
    let width = Math.max(handWidth, bbox.width) + 2.5;
    if (spec.mode === 'draw') {
      // The boneyard sits at -BONEYARD_X with width DOM_LONG; widen the
      // frustum so it stays visible without cropping.
      const minX = -Math.abs(BONEYARD_X) - DOM_LONG / 2 - 0.4;
      const maxX = Math.max(width / 2, bbox.width / 2 + 1);
      width = Math.max(width, (maxX - minX));
    }
    const depth = Math.max(handDepth, bbox.depth + handDepth * 0.5) + 1.6;
    setBounds({ width, depth, centerX: 0, centerZ: 0, baselineElevationDeg: 64 });
  }
  updateBounds();

  // Shadow pass — sized generously so growing chains stay covered.
  const shadowPass = createShadowPass({
    renderer,
    scene,
    area: { width: 26, depth: 14, centerX: 0, centerZ: 0 },
    resolution: 1024,
    height: 4,
    blur: 5.0,
    darkness: 1.0,
    opacity: 0.5,
  });
  scene.add(shadowPass.plane);
  let shadowDirty = true;

  // ── End-of-chain pickers (shown when a tile matches both ends) ──────
  // Subtle drop-zone rectangle the size of a tile slot — the click target.
  // Calmer than the pulsing ring we had before, and the wider hit area
  // means the player doesn't need pixel-precise aim.
  const markerGeom = new PlaneGeometry(DOM_LONG + 0.35, DOM_SHORT + 0.35);
  markerGeom.rotateX(-Math.PI / 2);
  const markerTexture = (() => {
    const W = 512, H = 256;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    const r = 28;
    const m = 14;
    const x0 = m, y0 = m, x1 = W - m, y1 = H - m;
    ctx.beginPath();
    ctx.moveTo(x0 + r, y0);
    ctx.lineTo(x1 - r, y0);
    ctx.quadraticCurveTo(x1, y0, x1, y0 + r);
    ctx.lineTo(x1, y1 - r);
    ctx.quadraticCurveTo(x1, y1, x1 - r, y1);
    ctx.lineTo(x0 + r, y1);
    ctx.quadraticCurveTo(x0, y1, x0, y1 - r);
    ctx.lineTo(x0, y0 + r);
    ctx.quadraticCurveTo(x0, y0, x0 + r, y0);
    ctx.closePath();
    ctx.fillStyle = 'rgba(200, 169, 106, 0.16)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(200, 169, 106, 0.55)';
    ctx.lineWidth = 3;
    ctx.stroke();
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    return tex;
  })();
  function makeMarker(side: Side): Mesh {
    const mat = new MeshBasicMaterial({
      map: markerTexture,
      transparent: true,
      depthWrite: false,
    });
    const m = new Mesh(markerGeom, mat);
    m.userData.endMarker = side;
    m.visible = false;
    m.renderOrder = 3;
    return m;
  }
  const markerLeft = makeMarker('left');
  const markerRight = makeMarker('right');
  scene.add(markerLeft, markerRight);

  // Each boneyard tile keeps its initial stack y-position so drawing one
  // doesn't reshuffle the whole pile — the stack just gets shorter from
  // the top. Captured at deal time, never re-keyed.
  const boneyardStackY = new Map<string, number>();

  // ── Mesh build ──────────────────────────────────────────────────────
  function buildMeshes() {
    for (const player of [0, 1] as const) {
      for (const d of state.hands[player]) {
        const faceMat: Material = createDominoFaceMaterial(d);
        const m = createDominoMesh(faceMat);
        m.userData.dominoId = d.id;
        m.traverse((c) => { c.userData.dominoId = d.id; });
        meshById.set(d.id, m);
        idByMesh.set(m, d.id);
        if (player === 1) setFaceDown(m, true);
        scene.add(m);
      }
    }
    // Boneyard tiles (draw mode only). Built face-down; revealed on draw.
    state.boneyard.forEach((d, i) => {
      const faceMat = createDominoFaceMaterial(d);
      const m = createDominoMesh(faceMat);
      m.userData.dominoId = d.id;
      m.userData.location = 'boneyard';
      m.traverse((c) => {
        c.userData.dominoId = d.id;
        c.userData.location = 'boneyard';
      });
      setFaceDown(m, true);
      meshById.set(d.id, m);
      idByMesh.set(m, d.id);
      dominoById.set(d.id, d);
      // Bottom of stack is the LAST tile drawn (highest engine index).
      // Top tile (engine index 0) sits at the highest y.
      const stackPos = (state.boneyard.length - 1 - i) * BONEYARD_STACK_OFFSET;
      boneyardStackY.set(d.id, DOM_D / 2 + stackPos);
      scene.add(m);
    });
  }

  function disposeMeshes() {
    for (const m of meshById.values()) {
      scene.remove(m);
      m.traverse((c) => {
        const mat = (c as { material?: Material | Material[] }).material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose?.());
        else if (mat && 'dispose' in mat) mat.dispose();
      });
    }
    meshById.clear();
    idByMesh.clear();
    dominoById.clear();
    boneyardStackY.clear();
  }

  // ── Position computation ────────────────────────────────────────────
  function targetForHandTile(d: Domino, player: Player): TileTarget {
    const hand = displayOrder[player];
    const idx = hand.findIndex((x) => x.id === d.id);
    const visIdx = idx === -1 ? 0 : idx;
    const handLen = hand.length;
    const x = (visIdx - (handLen - 1) / 2) * DOM_PITCH_SHORT;
    const z = player === 0 ? PLAYER_Z : CPU_Z;
    // Vertical orientation — long axis along z. Rotate +90° (CCW from +y).
    // Player tile: low half ends up at +z (front, closer to viewer).
    // CPU tile is face-down so orientation doesn't read; keep symmetric
    // (rotate -π/2 so visually mirror-flipped — purely aesthetic).
    const rotY = player === 0 ? Math.PI / 2 : -Math.PI / 2;
    return { x, y: DOM_D / 2, z, rotY, faceDown: player === 1 };
  }

  function targetForChainTile(d: Domino): TileTarget | null {
    const placements = state.chain.placements;
    const idx = placements.findIndex((p) => p.domino.id === d.id);
    if (idx === -1) return null;
    const slot = chainSlots[idx];
    if (!slot) return null;
    return { x: slot.x, y: DOM_D / 2, z: slot.z, rotY: slot.rotY, faceDown: false };
  }

  function targetForBoneyard(id: string): TileTarget | null {
    if (!state.boneyard.some((d) => d.id === id)) return null;
    const stackY = boneyardStackY.get(id) ?? DOM_D / 2;
    return { x: BONEYARD_X, y: stackY, z: BONEYARD_Z, rotY: 0, faceDown: true };
  }

  function targetFor(id: string): TileTarget {
    const d = dominoById.get(id)!;
    const inChain = targetForChainTile(d);
    if (inChain) return inChain;
    const inBoneyard = targetForBoneyard(id);
    if (inBoneyard) return inBoneyard;
    if (state.hands[0].some((x) => x.id === id)) return targetForHandTile(d, 0);
    return targetForHandTile(d, 1);
  }

  function placeAllInstant() {
    for (const [id, mesh] of meshById) {
      const t = targetFor(id);
      mesh.position.set(t.x, t.y, t.z);
      mesh.rotation.set(0, t.rotY, 0);
      mesh.scale.setScalar(1);
      setFaceDown(mesh, t.faceDown);
    }
    shadowDirty = true;
  }

  // ── Animation helpers ───────────────────────────────────────────────
  function animateTileToTarget(id: string, durationMs: number) {
    const mesh = meshById.get(id);
    if (!mesh) return;
    const t = targetFor(id);
    if (t.faceDown !== ((mesh.userData.materials as { face: { visible: boolean } } | undefined)?.face.visible === false)) {
      setFaceDown(mesh, t.faceDown);
    } else {
      setFaceDown(mesh, t.faceDown);
    }
    tweenArc(mesh, { x: t.x, y: t.y, z: t.z }, durationMs, 0.55);
    tweenRotation(mesh, { y: t.rotY }, durationMs);
    shadowDirty = true;
  }

  // ── HUD ─────────────────────────────────────────────────────────────
  const hud = createDominoHud({
    onNewGame: () => newGame(),
    onReplay: () => newGame(),
  });

  function refreshHud() {
    hud.setCounts(state.hands[0].length, state.hands[1].length);
    hud.setEnds(chainLeftEnd(state.chain), chainRightEnd(state.chain));
  }

  function setStatus(text: string | null) {
    hud.setStatus(text);
  }

  function showResult() {
    if (state.result === 'empty') {
      const won = state.winner === 0;
      hud.showWin(won ? 'You won' : 'CPU won', won ? 'cleared the hand' : 'opponent cleared first');
    } else if (state.result === 'blocked') {
      const youTotal = handPipTotal(state.hands[0]);
      const cpuTotal = handPipTotal(state.hands[1]);
      const won = state.winner === 0;
      const sub = won
        ? `blocked · you ${youTotal} vs cpu ${cpuTotal}`
        : `blocked · cpu ${cpuTotal} vs you ${youTotal}`;
      hud.showWin(won ? 'You won' : 'CPU won', sub);
    } else if (state.result === 'draw') {
      hud.showWin('Draw', 'blocked · equal pip totals');
    }
  }

  // ── Turn flow ───────────────────────────────────────────────────────
  function afterStateChange() {
    refreshHud();
    if (state.winner !== null || state.result !== null) {
      setStatus(null);
      showResult();
      return;
    }
    if (state.turn === 1) {
      setStatus('CPU thinking…');
      setTimeout(scheduleCpuTurn, CPU_DELAY_MS);
    } else {
      if (state.requiredOpener && state.requiredOpener.player === 0) {
        const t = state.requiredOpener.tile;
        setStatus(`Opener: play ${t.low}|${t.high}`);
        return;
      }
      if (mustDraw(state)) {
        setStatus('No moves — draw a tile');
        return;
      }
      if (mustPass(state)) {
        setStatus('No moves — pass');
        setTimeout(() => {
          state = tryPass(state);
          afterStateChange();
        }, PASS_DELAY_MS);
      } else {
        setStatus('Your turn');
      }
    }
  }

  function scheduleCpuTurn() {
    if (state.winner !== null || state.result !== null) return;
    // CPU draws first if it has no plays and the boneyard isn't empty.
    if (mustDraw(state)) {
      setStatus('CPU draws…');
      doCpuDraw();
      return;
    }
    const move = pickCpuMove(state, 1);
    if (move.kind === 'pass') {
      setStatus('CPU passes');
      state = tryPass(state);
      setTimeout(() => { afterStateChange(); }, PASS_DELAY_MS);
      return;
    }
    busy = true;
    state = tryPlay(state, move.dominoId, move.side);
    recomputeChain();
    updateBounds();
    animateTileToTarget(move.dominoId, PLAY_DURATION_MS);
    relayoutChainTiles(PLAY_DURATION_MS);
    playMatch();
    setTimeout(() => {
      busy = false;
      afterStateChange();
    }, PLAY_DURATION_MS + 30);
  }

  /** CPU draws the top boneyard tile face-down to its hand, then re-tries. */
  function doCpuDraw() {
    const drawnTile = state.boneyard[0];
    if (!drawnTile) return;
    busy = true;
    state = tryDraw(state);
    appendToDisplayOrder(1, drawnTile.id);
    boneyardStackY.delete(drawnTile.id);
    const mesh = meshById.get(drawnTile.id);
    if (mesh) {
      // Stays face-down; just slides into the CPU hand.
      const t = targetForHandTile(drawnTile, 1);
      tweenArc(mesh, { x: t.x, y: t.y, z: t.z }, PLAY_DURATION_MS, 0.45);
      tweenRotation(mesh, { y: t.rotY }, PLAY_DURATION_MS);
    }
    setTimeout(() => {
      busy = false;
      // Loop back: CPU may need to draw again, or now has a play.
      scheduleCpuTurn();
    }, PLAY_DURATION_MS + 40);
  }

  function appendToDisplayOrder(player: Player, dominoId: string) {
    const tile = dominoById.get(dominoId);
    if (!tile) return;
    if (displayOrder[player].some((d) => d.id === dominoId)) return;
    displayOrder[player] = [...displayOrder[player], tile];
  }

  function relayoutChainTiles(durationMs: number) {
    // When a tile is added (especially on the left), every other chain
    // tile's center shifts. Tween them to their new positions.
    for (const p of state.chain.placements) {
      const mesh = meshById.get(p.domino.id);
      if (!mesh) continue;
      const t = targetForChainTile(p.domino);
      if (!t) continue;
      tweenTo(mesh, { x: t.x, y: t.y, z: t.z }, durationMs);
      tweenRotation(mesh, { y: t.rotY }, durationMs);
    }
  }

  // ── Two-end picker (when a tile matches both chain ends) ───────────
  let selectedId: string | null = null;

  /** Where would each end of the chain be after playing `tile` there?
   *  Computed in the CURRENT chain coordinate frame so the markers can be
   *  shown without disturbing the on-table tile positions. */
  interface GhostPos { x: number; z: number; rotY: number }
  function ghostEndPositions(tile: Domino): {
    left: GhostPos | null;
    right: GhostPos | null;
  } {
    const opts = CHAIN_LAYOUT_OPTS;
    const base = layoutChain2D(state.chain.placements, opts);
    let left: GhostPos | null = null;
    let right: GhostPos | null = null;

    if (canPlayLeft(state.chain, tile)) {
      const ghost = chainPlayLeft(state.chain, tile);
      const gl = layoutChain2D(ghost.placements, opts);
      if (state.chain.placements.length === 0) {
        const s = gl.slots[0]!;
        left = { x: s.x, z: s.z, rotY: s.rotY };
      } else {
        // playLeft prepends → ghost slot 1 corresponds to base slot 0.
        const dx = base.slots[0]!.x - gl.slots[1]!.x;
        const dz = base.slots[0]!.z - gl.slots[1]!.z;
        const s = gl.slots[0]!;
        left = { x: s.x + dx, z: s.z + dz, rotY: s.rotY };
      }
    }
    if (canPlayRight(state.chain, tile)) {
      const ghost = chainPlayRight(state.chain, tile);
      const gr = layoutChain2D(ghost.placements, opts);
      if (state.chain.placements.length === 0) {
        const s = gr.slots[0]!;
        right = { x: s.x, z: s.z, rotY: s.rotY };
      } else {
        // playRight appends → ghost slot 0 corresponds to base slot 0.
        const dx = base.slots[0]!.x - gr.slots[0]!.x;
        const dz = base.slots[0]!.z - gr.slots[0]!.z;
        const last = gr.slots[gr.slots.length - 1]!;
        right = { x: last.x + dx, z: last.z + dz, rotY: last.rotY };
      }
    }
    return { left, right };
  }

  function showMarkersFor(tile: Domino) {
    const pos = ghostEndPositions(tile);
    if (pos.left) {
      markerLeft.position.set(pos.left.x, 0.012, pos.left.z);
      markerLeft.rotation.y = pos.left.rotY;
      markerLeft.visible = true;
    } else markerLeft.visible = false;
    if (pos.right) {
      markerRight.position.set(pos.right.x, 0.012, pos.right.z);
      markerRight.rotation.y = pos.right.rotY;
      markerRight.visible = true;
    } else markerRight.visible = false;
  }

  function hideMarkers() {
    markerLeft.visible = false;
    markerRight.visible = false;
  }

  function setSelectedTile(id: string | null) {
    if (selectedId === id) return;
    if (selectedId) {
      const m = meshById.get(selectedId);
      if (m) {
        tweenTo(m, { y: DOM_D / 2 }, 160);
        tweenScale(m, 1.0, 160);
      }
    }
    selectedId = id;
    if (id) {
      const m = meshById.get(id);
      if (m) {
        tweenTo(m, { y: DOM_D / 2 + SELECT_LIFT }, 160);
        tweenScale(m, 1.06, 160);
      }
      const tile = dominoById.get(id);
      if (tile) showMarkersFor(tile);
    } else {
      hideMarkers();
    }
  }

  function playerPlay(id: string) {
    const legal = legalPlaysFor(state, 0).find((p) => p.domino.id === id);
    if (!legal) {
      playInvalid();
      return;
    }
    if (legal.sides.length === 1) {
      setSelectedTile(null);
      doPlayerMove(id, legal.sides[0]!);
      return;
    }
    // Two ends accept this tile — toggle selection so the player can pick.
    if (selectedId === id) {
      setSelectedTile(null);
      return;
    }
    setSelectedTile(id);
    setStatus('Pick an end');
    playSelect();
  }

  function doPlayerMove(id: string, side: Side) {
    busy = true;
    hud.startTimer();
    state = tryPlay(state, id, side);
    recomputeChain();
    updateBounds();
    animateTileToTarget(id, PLAY_DURATION_MS);
    relayoutChainTiles(PLAY_DURATION_MS);
    playMatch();
    setTimeout(() => {
      busy = false;
      afterStateChange();
    }, PLAY_DURATION_MS + 30);
  }

  /** Player draws the top boneyard tile face-up to their hand. */
  function doPlayerDraw() {
    if (!mustDraw(state)) return;
    const drawnTile = state.boneyard[0];
    if (!drawnTile) return;
    busy = true;
    hud.startTimer();
    state = tryDraw(state);
    appendToDisplayOrder(0, drawnTile.id);
    boneyardStackY.delete(drawnTile.id);
    const mesh = meshById.get(drawnTile.id);
    if (mesh) {
      // Reveal the face as it leaves the boneyard.
      setFaceDown(mesh, false);
      const t = targetForHandTile(drawnTile, 0);
      tweenArc(mesh, { x: t.x, y: t.y, z: t.z }, PLAY_DURATION_MS, 0.55);
      tweenRotation(mesh, { y: t.rotY }, PLAY_DURATION_MS);
    }
    playSelect();
    setTimeout(() => {
      busy = false;
      // After a draw the player keeps the turn — re-evaluate state.
      afterStateChange();
    }, PLAY_DURATION_MS + 40);
  }

  // ── Pointer ─────────────────────────────────────────────────────────
  let hoveredId: string | null = null;

  createPointerPicker({
    canvas,
    camera,
    onTap: (_e, ray) => {
      if (busy || state.winner !== null || state.result !== null) return;
      if (state.turn !== 0) return;

      // Marker click takes priority — finalize a pending two-end pick.
      if (selectedId) {
        const visibleMarkers: Object3D[] = [];
        if (markerLeft.visible) visibleMarkers.push(markerLeft);
        if (markerRight.visible) visibleMarkers.push(markerRight);
        if (visibleMarkers.length > 0) {
          const mhits = ray.intersectObjects(visibleMarkers, false);
          if (mhits.length > 0) {
            const which = mhits[0]!.object.userData.endMarker as Side;
            const id = selectedId;
            setSelectedTile(null);
            doPlayerMove(id, which);
            return;
          }
        }
      }

      const hits = ray.intersectObjects(Array.from(meshById.values()), true);
      if (hits.length === 0) {
        if (selectedId) setSelectedTile(null);
        return;
      }
      const mesh = findUserDataAncestor(hits[0]!.object, 'dominoId');
      if (!mesh) return;
      const id = idByMesh.get(mesh);
      if (!id) return;

      // Boneyard click → draw (only when the player must draw).
      if (state.boneyard.some((d) => d.id === id)) {
        if (mustDraw(state)) {
          if (selectedId) setSelectedTile(null);
          doPlayerDraw();
        } else {
          playInvalid();
        }
        return;
      }
      if (!state.hands[0].some((d) => d.id === id)) {
        playInvalid();
        return;
      }
      playerPlay(id);
    },
    onHover: (_e, ray) => {
      if (busy || state.turn !== 0 || state.winner !== null) return;
      const hits = ray.intersectObjects(Array.from(meshById.values()), true);
      let id: string | null = null;
      if (hits.length > 0) {
        const mesh = findUserDataAncestor(hits[0]!.object, 'dominoId');
        if (mesh) {
          const hitId = idByMesh.get(mesh);
          if (hitId && state.hands[0].some((d) => d.id === hitId)) {
            const legal = legalPlaysFor(state, 0).some((p) => p.domino.id === hitId);
            if (legal) id = hitId;
          }
        }
      }
      if (id !== hoveredId) {
        // Don't lower the currently-selected tile — it's lifted higher by
        // setSelectedTile and stays there until the player decides.
        if (hoveredId && hoveredId !== selectedId) {
          const m = meshById.get(hoveredId);
          if (m) {
            tweenTo(m, { y: DOM_D / 2 }, 140);
            tweenScale(m, 1.0, 140);
          }
        }
        if (id && id !== selectedId) {
          const m = meshById.get(id);
          if (m) {
            tweenTo(m, { y: DOM_D / 2 + HAND_HOVER_LIFT }, 140);
            tweenScale(m, 1.03, 140);
          }
        }
        hoveredId = id;
      }
    },
  });

  window.addEventListener('pointerdown', () => unlockAudio(), { once: true });

  // ── New game ─────────────────────────────────────────────────────────
  function newGame() {
    setSelectedTile(null);
    disposeMeshes();
    state = dealGame({ mode: spec.mode });
    displayOrder = [sortHand(state.hands[0]), sortHand(state.hands[1])];
    for (const d of [...state.hands[0], ...state.hands[1], ...state.boneyard]) {
      dominoById.set(d.id, d);
    }
    buildMeshes();
    recomputeChain();
    placeAllInstant();
    updateBounds();
    refreshHud();
    hud.resetTimer();
    hud.hideWin();
    playShuffle();
    afterStateChange();
  }

  // ── Initial deal ─────────────────────────────────────────────────────
  buildMeshes();
  placeAllInstant();
  refreshHud();
  setTimeout(() => playShuffle(), 60);
  // Kick off the first turn (CPU may need to open).
  setTimeout(() => afterStateChange(), 250);

  // Test hook (mirrors mahjong's __mj).
  (window as unknown as { __dom?: unknown }).__dom = {
    state: () => state,
    legal: () => legalPlaysFor(state, 0).map((p) => ({ id: p.domino.id, sides: p.sides })),
    counts: () => ({ you: state.hands[0].length, cpu: state.hands[1].length }),
  };

  // ── Render loop ──────────────────────────────────────────────────────
  let prevActive = false;
  function tick(t: number) {
    updateTweens(t);

    const active = hasActiveTweens();
    if (shadowDirty || active || prevActive) {
      shadowPass.update();
      renderer.shadowMap.needsUpdate = true;
      shadowDirty = active;
    }
    prevActive = active;
    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}
